import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';

const COUNTDOWN_SECONDS = 120;

export default function SessionExpiryDialog({ open, onExtend, onLogout }) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [extending, setExtending] = useState(false);
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setSecondsLeft(COUNTDOWN_SECONDS);
      setExtending(false);
      expiredRef.current = false;
      return;
    }
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (!expiredRef.current) {
            expiredRef.current = true;
            onLogout();
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open, onLogout]);

  const handleExtend = async () => {
    setExtending(true);
    try {
      await onExtend();
    } finally {
      setExtending(false);
    }
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const progress = (secondsLeft / COUNTDOWN_SECONDS) * 100;

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle>Session expired</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Your session has expired. Extend it to keep working, or you'll be signed out automatically.
        </Typography>
        <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h4" fontWeight={600} color={secondsLeft <= 30 ? 'error' : 'primary'}>
            {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            until automatic sign-out
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={secondsLeft <= 30 ? 'error' : 'primary'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onLogout} disabled={extending}>
          Log out
        </Button>
        <Button variant="contained" onClick={handleExtend} disabled={extending}>
          {extending ? 'Extending...' : 'Extend session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
